import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Image,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useSelector } from "react-redux";
import { translateText } from "../../translations/translationService";
import { BUTTON_PERMISSIONS } from "../../constants/Constants";
import {
  versionMode,
  backgroundColor2,
  gradientButton,
} from "../../constants/Constants";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  BRAND_COLORS,
  SHADOW_COLORS,
  TEXT_COLORS,
} from "../../style/colors.js";

const categoryDecorationImages = {
  "Información": require("../../assets/inf.png"),
  "Producción": require("../../assets/tuerca.png"),
  "Control de Pesos": require("../../assets/peso.png"),
  "Control de Piedras": require("../../assets/diamond.png"),
  "Calidad": require("../../assets/tablet.png"),
  "Administración": require("../../assets/social.png"),
};

const gradientColors = [backgroundColor2, gradientButton];

const hasPermission = (userPermissions, buttonId) => {
  if (!userPermissions) return false;
  const permissions = userPermissions.split(",").map(Number);
  return permissions.includes(buttonId);
};

const isProductionOrTest = () =>
  versionMode === "Test" || versionMode === "Produccion";
const isTestOnly = () => versionMode === "Test";

const ProfessionalButton = ({ button, lang, index }) => {
  const animatedValue = useRef(new Animated.Value(0)).current;
  const scaleValue = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Quitar el delay para que todos aparezcan al mismo tiempo
    Animated.timing(animatedValue, {
      toValue: 1,
      duration: 250, // Duración más corta (era 300ms)
      useNativeDriver: true,
    }).start();
  }, [index]);

  const handlePressIn = () => {
    Animated.timing(scaleValue, {
      toValue: 0.95,
      duration: 100,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.timing(scaleValue, {
      toValue: 1,
      duration: 100,
      useNativeDriver: true,
    }).start();
  };

  const translateY = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [30, 0],
  });

  const opacity = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  return (
    <Animated.View
      style={[
        styles.buttonWrapper,
        {
          opacity,
          transform: [{ translateY }, { scale: scaleValue }],
        },
      ]}>
      <TouchableOpacity
        onPress={button.onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        activeOpacity={1}
        accessible={true}
        accessibilityLabel={translateText(button.title, lang)}
        accessibilityRole='button'
        style={styles.touchableArea}>
        <LinearGradient
          colors={gradientColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.buttonContainer}>
          {/* Overlay sutil para profundidad */}
          <View style={styles.overlayTop} />

          {/* Contenido del botón */}
          <View style={styles.contentContainer}>
            {/* Icono */}
            <View style={styles.iconContainer}>
              <MaterialCommunityIcons
                name={button.iconName}
                size={36}
                color='white'
                style={styles.icon}
              />
            </View>

            {/* Texto */}
            <Text style={styles.buttonText} numberOfLines={2}>
              {translateText(button.title, lang)}
            </Text>
          </View>

          {/* Sutil highlight en el borde */}
          <View style={styles.borderHighlight} />
        </LinearGradient>
      </TouchableOpacity>
    </Animated.View>
  );
};

export const ButtonList = ({ buttons, isMainMenu = false, isAdminMenu = false }) => {
  const lang = useSelector((state) => state.lang.value);

  // Agrupar botones por categoría
  const groupedButtons = useMemo(() => {
    const groups = {};
    buttons.forEach((button) => {
      // Si es AdminMenu, agrupar todo en "Administración"
      // Si no tiene categoría y no es AdminMenu, no agrupar (para botones sin categoría como buttonsTop)
      const category = isAdminMenu ? "Administración" : (button.category || null);
      if (category) {
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(button);
      } else {
        // Botones sin categoría (como buttonsTop) - ponerlos en un grupo especial
        if (!groups["_noCategory"]) {
          groups["_noCategory"] = [];
        }
        groups["_noCategory"].push(button);
      }
    });
    return groups;
  }, [buttons, isAdminMenu]);

  const categoryOrder = ["Información", "Producción", "Control de Pesos", "Control de Piedras", "Calidad", "Procesos", "Administración", "Otros"];
  
  // Filtrar categorías que tienen botones
  const hasNoCategoryButtons = groupedButtons["_noCategory"] && groupedButtons["_noCategory"].length > 0;
  const activeCategories = categoryOrder.filter(cat => groupedButtons[cat] && groupedButtons[cat].length > 0);
  
  // Organizar layout según si es menú principal o no
  let column1Categories, column2Categories, column3Categories;
  
  if (isMainMenu) {
    // Organizar en 3 columnas: Primera fila (Información, Producción, Control de Pesos), Segunda fila (Control de Piedras, Calidad, Administración)
    const firstRowCategories = ["Información", "Producción", "Control de Pesos"].filter(cat => activeCategories.includes(cat));
    const secondRowCategories = ["Control de Piedras", "Calidad", "Administración"].filter(cat => activeCategories.includes(cat));
    
    // Dividir en 3 columnas verticales
    // Columna 1: Información (fila 1), Control de Piedras (fila 2)
    // Columna 2: Producción (fila 1), Calidad (fila 2)
    // Columna 3: Control de Pesos (fila 1), Administración (fila 2)
    column1Categories = [
      firstRowCategories[0],
      secondRowCategories[0]
    ].filter(Boolean);
    column2Categories = [
      firstRowCategories[1],
      secondRowCategories[1]
    ].filter(Boolean);
    column3Categories = [
      firstRowCategories[2],
      secondRowCategories[2]
    ].filter(Boolean);
  } else {
    // Layout original de 2 columnas para otros usos
    const columns = 2;
    const categoriesPerColumn = Math.ceil(activeCategories.length / columns);
    column1Categories = activeCategories.slice(0, categoriesPerColumn);
    column2Categories = activeCategories.slice(categoriesPerColumn);
    column3Categories = [];
  }

  // Mapeo de iconos para cada categoría
  const categoryIcons = {
    "Información": "information",
    "Producción": "factory",
    "Control de Pesos": "scale-balance",
    "Control de Piedras": "diamond-stone",
    "Calidad": "check-circle",
    "Procesos": "cog",
    "Administración": "account-tie",
    "Otros": "dots-horizontal",
  };

  const renderCategory = (category) => {
    const categoryButtons = groupedButtons[category];
    if (!categoryButtons || categoryButtons.length === 0) return null;
    const decorationImage = categoryDecorationImages[category];
    const decorationStyle =
      category === "Producción"
        ? styles.productionDecoration
        : category === "Control de Piedras"
          ? styles.stonesDecoration
          : category === "Administración"
            ? styles.adminDecoration
        : styles.categoryDecoration;

    // Si es el menú principal o admin menu, usar el nuevo estilo de tarjetas
    if (isMainMenu || isAdminMenu) {
      return (
        <View key={category} style={styles.categorySection}>
          <View style={styles.categoryHeader}>
            <MaterialCommunityIcons
              name={categoryIcons[category] || "dots-horizontal"}
              size={20}
              color={TEXT_COLORS.LIGHT}
              style={styles.categoryIcon}
            />
            <Text style={styles.categoryTitle}>{translateText(category, lang)}</Text>
          </View>
          <View style={styles.menuItemsContainer}>
            {decorationImage ? (
              <Image
                source={decorationImage}
                style={decorationStyle}
                resizeMode='contain'
              />
            ) : null}
            {categoryButtons.map((button, index) => {
              const [isHovered, setIsHovered] = useState(false);
              return (
                <TouchableOpacity
                  key={`item-${category}-${index}`}
                  style={[
                    styles.menuItem,
                    isHovered && styles.menuItemHovered
                  ]}
                  onPress={button.onPress}
                  activeOpacity={0.7}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  onPressIn={() => setIsHovered(true)}
                  onPressOut={() => setIsHovered(false)}>
                  <MaterialCommunityIcons
                    name={button.iconName}
                    size={20}
                    color={BRAND_COLORS.ACCENT}
                    style={styles.menuItemIcon}
                  />
                  <Text style={[
                    styles.menuItemText,
                    isHovered && styles.menuItemTextHovered
                  ]}>
                    {translateText(button.title, lang)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      );
    }

    // Estilo original para otros usos (botones grandes)
    // Si la categoría es "_noCategory", no mostrar header
    if (category === "_noCategory") {
      return (
        <View key={category} style={styles.buttonListContainerNoCategory}>
          {categoryButtons.map((button, index) => (
            <ProfessionalButton
              key={`button-${category}-${index}`}
              button={button}
              lang={lang}
              index={index}
            />
          ))}
        </View>
      );
    }
    
    return (
      <View key={category} style={styles.categorySectionOld}>
        <View style={styles.categoryHeaderOld}>
          <Text style={styles.categoryTitleOld}>{category}</Text>
        </View>
        <View style={styles.buttonListContainer}>
          {categoryButtons.map((button, index) => (
            <ProfessionalButton
              key={`button-${category}-${index}`}
              button={button}
              lang={lang}
              index={index}
            />
          ))}
        </View>
      </View>
    );
  };

  // Si es AdminMenu, renderizar una sola tarjeta centrada
  if (isAdminMenu) {
    return (
      <View style={styles.groupedContainerAdmin}>
        <View style={styles.adminCardContainer}>
          {activeCategories.map(renderCategory)}
        </View>
      </View>
    );
  }

  // Si hay botones sin categoría (como buttonsTop), renderizarlos sin estructura de categorías
  if (hasNoCategoryButtons && !isMainMenu && !isAdminMenu) {
    return (
      <View style={styles.groupedContainer}>
        {renderCategory("_noCategory")}
      </View>
    );
  }

  return (
    <View style={styles.groupedContainer}>
      <View style={isMainMenu ? styles.columnsContainer : styles.columnsContainerOld}>
        <View style={isMainMenu ? styles.column : styles.columnOld}>
          {column1Categories.map(renderCategory)}
        </View>
        <View style={isMainMenu ? styles.column : styles.columnOld}>
          {column2Categories.map(renderCategory)}
        </View>
        {isMainMenu && column3Categories.length > 0 && (
          <View style={styles.column}>
            {column3Categories.map(renderCategory)}
          </View>
        )}
      </View>
    </View>
  );
};

const createButton = (title, iconName, onPress) => ({
  title,
  iconName,
  onPress,
});

export const buttonsTop = ({
  infoData,
  navigation,
  lang,
  setIsModalVisible,
  equipo,
  screenName,
  nivelAcceso,
}) => {
  return useMemo(() => {
    if (!isProductionOrTest()) return [];

    const buttons = [
      createButton("Tiempo y Estado", "chart-timeline-variant", () =>
        navigation.navigate("TimeAndStatus", { infoData })
      ),
      createButton("Tiempos de Trabajo", "clock-outline", () =>
        navigation.navigate("WorkingTimes", { infoData })
      ),
      createButton("Entrega de Piedra", "diamond-stone", () =>
        navigation.navigate("Stone delivery", { infoData })
      ),
      createButton("Entrega de Fornituras", "package-variant", () =>
        navigation.navigate("Delivery Forniture", { infoData })
      ),
      createButton("Engaste", "ring", () =>
        navigation.navigate("Crimping", { infoData })
      ),
    ];

    if (hasPermission(nivelAcceso, BUTTON_PERMISSIONS.CONTROL_CALIDAD)) {
      buttons.push(
        createButton("Control de Calidad", "check-circle", () =>
          setIsModalVisible(true)
        )
      );
    }

    return buttons;
  }, [
    infoData,
    navigation,
    lang,
    setIsModalVisible,
    equipo,
    screenName,
    nivelAcceso,
  ]);
};
export const buttonsDown = ({ infoData, navigation, lang }) => {
  return useMemo(() => {
    return [];
  }, [infoData, navigation, lang]);
};

export const buttonsAdminMenu = ({ navigation, nivelAcceso }) => {
  return useMemo(() => {
    const buttons = [];

    if (isProductionOrTest()) {
      buttons.push(
        createButton("Consultar Tiempos", "timer-settings-outline", () =>
          navigation.navigate("Daily Time Report")
        ),
        createButton("Carga de Fábrica", "factory", () =>
          navigation.navigate("Carga de Fabrica")
        ),
        createButton("Estado Operarios", "table-account", () =>
          navigation.navigate("Operator administration panel")
        )
      );
    }

    if (isTestOnly()) {
      buttons.push(
        createButton("Ajustar Tiempo trabajado", "clock-edit-outline", () =>
          navigation.navigate("")
        ),
        createButton("Ajustar tiempo Standard", "timer-cog-outline", () =>
          navigation.navigate("Ajustar Téoricos")
        ),
        createButton("Control del oro", "gold", () => navigation.navigate(""))
      );
    }

    return buttons;
  }, [navigation, nivelAcceso]);
};

export const buttonsMenu = ({ navigation, equipo, nivelAcceso }) => {
  return useMemo(() => {
    if (!isProductionOrTest() || !equipo) return [];

    const allButtons = [
      {
        id: BUTTON_PERMISSIONS.ESTADO_GENERAL,
        category: "Información",
        ...createButton("Estado General", "view-dashboard", () =>
          navigation.navigate("Overall status")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.AGRUPACIONES,
        category: "Información",
        ...createButton("Agrupaciones", "folder-multiple", () =>
          navigation.navigate("Completed foundries")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.DOCUMENTACION,
        category: "Información",
        ...createButton("Documentación", "file-document-multiple", () =>
          navigation.navigate("Documentación")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.INFORME_ASIGNACION_PRECIOS,
        category: "Información",
        ...createButton(
          "Informe WO Externas",
          "file-chart",
          () => navigation.navigate("Assignment price lines report")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.FUNDICION,
        category: "Producción",
        ...createButton("Fundición", "fire", () =>
          navigation.navigate("Foundry")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.ASIGNACION,
        category: "Producción",
        ...createButton("Asignación", "clipboard-text", () =>
          navigation.navigate("Parts Movements")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.REPARACIONES,
        category: "Producción",
        ...createButton("Reparaciones", "wrench", () =>
          navigation.navigate("Reparaciones")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.TRABAJO_EXTERNO,
        category: "Producción",
        ...createButton("Trabajo Externo", "handshake", () =>
          navigation.navigate("Trabajo Externo")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.PESAJE_INICIAL,
        category: "Control de Pesos",
        ...createButton("Pesaje Inicial", "scale-balance", () =>
          navigation.navigate("Classification weighing")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.PESAJE_REPASO,
        category: "Control de Pesos",
        ...createButton("Pesaje Repaso", "scale", () =>
          navigation.navigate("Intermediate weighing")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.PESAJE_FORNITURAS,
        category: "Control de Pesos",
        ...createButton("Pesaje Fornituras", "link-variant", () =>
          navigation.navigate("Findings Weighing")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.PESAJE_FINAL,
        category: "Control de Pesos",
        ...createButton("Pesaje Final", "scale-balance", () =>
          navigation.navigate("Final weighing")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.PIEDRAS,
        category: "Control de Piedras",
        ...createButton("Piedras", "diamond-stone", () =>
          navigation.navigate("Piedras")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.PIEDRAS_ENTREGAR,
        category: "Control de Piedras",
        ...createButton("Deliver Gems", "swap-horizontal", () =>
          navigation.navigate("Deliver Stones")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.VALIDAR_CALIDAD,
        category: "Calidad",
        ...createButton("Validar Calidad", "check-decagram", () =>
          navigation.navigate("Validar calidad")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.ESCANER,
        category: "Calidad",
        ...createButton("Escáner", "barcode-scan", () =>
          navigation.navigate("Scan")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.MECANIZADO,
        category: "Producción",
        ...createButton("Mecanizado", "cog", () =>
          navigation.navigate("Machining")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.ADMINISTRACION,
        category: "Administración",
        ...createButton("Consultar Tiempos", "timer-settings-outline", () =>
          navigation.navigate("Daily Time Report")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.CARGA_FABRICA,
        category: "Administración",
        ...createButton("Carga de Fábrica", "factory", () =>
          navigation.navigate("Carga de Fabrica")
        ),
      },
      {
        id: BUTTON_PERMISSIONS.ADMINISTRACION,
        category: "Administración",
        ...createButton("Estado Operarios", "table-account", () =>
          navigation.navigate("Operator administration panel")
        ),
      },
    ];

    const filteredButtons = allButtons.filter((button) =>
      hasPermission(nivelAcceso, button.id)
    );

    return filteredButtons.map(({ id, category, ...button }) => ({ ...button, category }));
  }, [navigation, equipo, nivelAcceso]);
};

const styles = StyleSheet.create({
  groupedContainer: {
    width: "100%",
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 25,
  },
  groupedContainerAdmin: {
    width: "100%",
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  adminCardContainer: {
    width: "100%",
    maxWidth: 500,
    alignItems: "center",
  },
  columnsContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: 16,
    width: "100%",
    flexWrap: "wrap",
  },
  column: {
    // width: "31%",
    minWidth: 250,
    maxWidth: 380,
    alignItems: "stretch",
  },
  categorySection: {
    marginBottom: 16,
    borderRadius: 12,
    backgroundColor: BACKGROUND_COLORS.PALE_BLUISH_GRAY,
    shadowColor: SHADOW_COLORS.PRIMARY,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 6,
    borderWidth: 1,
    borderColor: BORDER_COLORS.PRIMARY,
    width: "100%",
    maxWidth: 380,
    padding: 0,
    overflow: "hidden",
    height: 280,
    display: "flex",
    flexDirection: "column",
  },
  categoryHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: BRAND_COLORS.ACCENT,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    gap: 10,
    borderBottomWidth: 2,
    borderBottomColor: BRAND_COLORS.ACCENT,
  },
  categoryIcon: {
    marginRight: 0,
  },
  categoryTitle: {
    fontSize: 12,
    fontWeight: "700",
    color: TEXT_COLORS.LIGHT,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    flex: 1,
  },
  menuItemsContainer: {
    backgroundColor: BACKGROUND_COLORS.PRIMARY,
    paddingTop: 8,
    paddingBottom: 12,
    paddingHorizontal: 16,
    flex: 1,
    justifyContent: "flex-start",
    position: "relative",
    overflow: "hidden",
  },
  productionDecoration: {
    position: "absolute",
    right: -28,
    bottom: 4,
    width: 150,
    height: 150,
    opacity: 0.15,
  },
  categoryDecoration: {
    position: "absolute",
    right: -20,
    bottom: -4,
    width: 150,
    height: 150,
    opacity: 0.18,
  },
  stonesDecoration: {
    position: "absolute",
    right: -20,
    bottom: -10,
    width: 150,
    height: 150,
    opacity: 0.15,
  },
  adminDecoration: {
    position: "absolute",
    right: -20,
    bottom: -2,
    width: 150,
    height: 150,
    opacity: 0.18,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginBottom: 2,
    backgroundColor: "transparent",
    transition: "all 0.2s ease",
    zIndex: 1,
  },
  menuItemHovered: {
    backgroundColor: "rgba(23, 64, 69, 0.08)",
    transform: [{ translateX: 4 }],
  },
  menuItemIcon: {
    marginRight: 12,
    width: 24,
    alignItems: "center",
    transition: "color 0.2s ease",
  },
  menuItemText: {
    fontSize: 14,
    color: BRAND_COLORS.ACCENT,
    fontWeight: "500",
    flex: 1,
    transition: "color 0.2s ease",
  },
  menuItemTextHovered: {
    color: BRAND_COLORS.ACCENT,
    fontWeight: "600",
  },
  // Estilos antiguos para otros usos (no menú principal)
  columnsContainerOld: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    width: "100%",
  },
  columnOld: {
    flex: 1,
    minWidth: 0,
  },
  categorySectionOld: {
    marginBottom: 8,
    paddingVertical: 2,
  },
  categoryHeaderOld: {
    marginBottom: 6,
    paddingHorizontal: 2,
  },
  categoryTitleOld: {
    fontSize: 10,
    fontWeight: "700",
    color: BRAND_COLORS.SECONDARY,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    backgroundColor: "rgba(70, 93, 100, 0.08)",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    textAlign: "center",
    overflow: "hidden",
  },
  buttonListContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 10,
    paddingHorizontal: 4,
  },
  buttonListContainerNoCategory: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 10,
    paddingHorizontal: 4,
    width: "100%",
  },
  buttonWrapper: {
    margin: 4,
    borderRadius: 16,
    shadowColor: SHADOW_COLORS.PRIMARY,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 8,
  },
  touchableArea: {
    borderRadius: 16,
    overflow: "hidden",
  },
  buttonContainer: {
    width: 150,
    height: 140,
    borderRadius: 16,
    position: "relative",
    overflow: "hidden",
  },
  overlayTop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "40%",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  contentContainer: {
    flex: 1,
    padding: 12,
    alignItems: "center",
    justifyContent: "space-between",
    zIndex: 2,
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    minHeight: 56,
  },
  icon: {
    textShadowColor: "rgba(0, 0, 0, 0.25)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 3,
  },
  buttonText: {
    fontSize: 14,
    fontFamily: "System",
    color: TEXT_COLORS.LIGHT,
    textAlign: "center",
    fontWeight: "600",
    lineHeight: 18,
    letterSpacing: 0.4,
    textShadowColor: "rgba(0, 0, 0, 0.35)",
    textShadowOffset: { width: 0, height: 1.5 },
    textShadowRadius: 2.5,
    minHeight: 36,
  },
  borderHighlight: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: "rgba(255, 255, 255, 0.2)",
    pointerEvents: "none",
  },
});
